import type { OnboardingDataItemType } from "@memorang/constants/config/schema";
import { chunkPairs } from "@memorang/helpers/array";
import { useTranslation } from "react-i18next";
import { Text, TouchableRipple } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import { AppIcon } from "../AppIcon";
import Box from "../Box";
import { useOnboarding } from "./OnboardingProvider";
import { ChangeLabel } from "./components/ChangeLabel";
import { OnboardingBox } from "./components/OnboardingBox";
import { resolveI18n } from "./helpers";

type Props = {
	title: string;
	description: string | undefined;
	data: OnboardingDataItemType[] | undefined;
};

type SkillCardProps = {
	item: OnboardingDataItemType;
	isSelected: boolean;
	locale: string;
	onPress: () => void;
};

const SkillCard = ({ item, isSelected, locale, onPress }: SkillCardProps) => {
	const theme = useAppTheme();

	return (
		<TouchableRipple
			onPress={onPress}
			borderless={false}
			style={{
				flex: 1,
				borderRadius: theme.roundness * 3,
				borderWidth: 1,
				borderColor: isSelected
					? theme.colors.secondary
					: theme.colors.surfaceVariant,
				backgroundColor: isSelected
					? theme.colors.elevation.level1
					: theme.colors.surface,
				overflow: "hidden",
			}}
		>
			<Box
				alignItems="center"
				gap={8}
				paddingHorizontal={10}
				paddingVertical={16}
			>
				<AppIcon
					name={(item.icon ?? "help-circle-outline") as never}
					size={22}
					color={
						isSelected ? theme.colors.secondary : theme.colors.inverseSurface
					}
				/>
				<Text
					variant="titleSmall"
					style={{
						flexShrink: 1,
					}}
					numberOfLines={2}
				>
					{resolveI18n(item.titlei18n, locale)}
				</Text>
			</Box>
		</TouchableRipple>
	);
};

export const OnboardingSkillScreen = ({
	title,
	description,
	data = [],
}: Props) => {
	const theme = useAppTheme();
	const { selectedSkillIds, onToggleSkill } = useOnboarding();
	const { t, i18n } = useTranslation();
	const locale = i18n.language;

	return (
		<Box gap={10}>
			<OnboardingBox>
				<Box gap={14}>
					<Text variant="titleLarge">{title}</Text>
					{description ? (
						<Text
							variant="bodyMedium"
							style={{ color: theme.colors.onSurfaceVariant }}
						>
							{description}
						</Text>
					) : null}
				</Box>
				<Box gap={8}>
					<Text
						variant="labelSmall"
						style={{ color: theme.colors.onSurfaceVariant }}
					>
						{t("onboarding.steps.skills.chooseAll")}
					</Text>
					<Box gap={8}>
						{chunkPairs(data).map((pair) => {
							const rowKey = pair
								.map(
									(item) =>
										item.type ?? resolveI18n(item.titlei18n, "en-US") ?? "",
								)
								.join("-");
							return (
								<Box key={rowKey} flexDirection="row" gap={8}>
									{pair.map((item) => {
										const id =
											item.type ?? resolveI18n(item.titlei18n, "en-US") ?? "";
										return (
											<SkillCard
												key={id}
												item={item}
												locale={locale}
												isSelected={selectedSkillIds.includes(id)}
												onPress={() => onToggleSkill(id)}
											/>
										);
									})}
									{pair.length === 1 ? <Box flex={1} /> : null}
								</Box>
							);
						})}
					</Box>
				</Box>
			</OnboardingBox>
			<ChangeLabel />
		</Box>
	);
};
